/** Recolhe do modelo carregado os dados que o Documento usa.
 *
 *  Vive à parte do modelo de dados (que é puro) porque é aqui que se
 *  fala com o viewer. Tudo o que falhe devolve o campo a null em vez
 *  de rebentar: um relatório com um campo por preencher é melhor do
 *  que nenhum relatório, e o campo por preencher aparece escrito no
 *  documento como `[chave: sem valor]`, por isso não passa
 *  despercebido.
 */

import { ehPesquisavel } from '@viewer/lib/entityFamily'
import { atributo } from '@core/layout/search/buildIndex'
import type { DadosDocumento } from '@viewer/report/documentModel'
import type { PinTopic } from '@viewer/bcf/lib/pinFilter'

interface ModeloLegivel {
  getItemsOfCategories: (
    padroes: RegExp[],
  ) => Promise<Record<string, number[] | undefined>>
  getItemsData: (
    ids: number[],
    opcoes: { attributesDefault: boolean },
  ) => Promise<unknown[]>
}

export interface ModeloParaRelatorio {
  id: string
  fileName: string
  schemaVersion: string | null
  entityCount: number
}

/** Conta os elementos por classe e apanha os nomes dos pisos.
 *
 *  Só conta o que é elemento, espaço ou grupo: um relatório que
 *  dissesse "14 mil entidades" contando relações objectivadas e
 *  property sets não diz nada a ninguém sobre o edifício. */
export async function recolherDoModelo(
  modelo: ModeloLegivel,
): Promise<{ porClasse: { classe: string; contagem: number }[]; pisos: string[] }> {
  const porClasse: { classe: string; contagem: number }[] = []
  const pisos: string[] = []

  const baldes = await modelo.getItemsOfCategories([/.*/])
  for (const [classe, ids] of Object.entries(baldes)) {
    if (!ids || ids.length === 0) continue
    if (classe.toUpperCase() === 'IFCBUILDINGSTOREY') {
      try {
        const dados = await modelo.getItemsData(ids, { attributesDefault: true })
        for (const d of dados) {
          const nome = atributo(d, 'Name')
          if (nome) pisos.push(nome)
        }
      } catch {
        // Sem nomes de piso, o relatório diz a contagem e não a lista.
      }
    }
    if (!ehPesquisavel(classe)) continue
    porClasse.push({ classe, contagem: ids.length })
  }

  porClasse.sort(
    (a, b) => b.contagem - a.contagem || a.classe.localeCompare(b.classe),
  )
  return { porClasse, pisos }
}

/** Junta tudo o que o documento precisa. */
export async function recolherDados(
  modelos: readonly ModeloParaRelatorio[],
  obterModelo: (id: string) => ModeloLegivel | null,
  topicos: readonly PinTopic[],
  agora: Date = new Date(),
): Promise<DadosDocumento> {
  const porClasse = new Map<string, number>()
  const pisos: string[] = []

  for (const m of modelos) {
    const modelo = obterModelo(m.id)
    if (!modelo) continue
    try {
      const r = await recolherDoModelo(modelo)
      for (const linha of r.porClasse) {
        porClasse.set(
          linha.classe,
          (porClasse.get(linha.classe) ?? 0) + linha.contagem,
        )
      }
      pisos.push(...r.pisos)
    } catch {
      // Um modelo que não deixe ler não impede os outros de contar.
    }
  }

  const primeiro = modelos[0] ?? null
  return {
    ficheiro: primeiro?.fileName ?? null,
    esquema: primeiro?.schemaVersion ?? null,
    // O nome do projecto sai do IfcProject e ainda não é lido aqui:
    // fica declarado como sem valor, que é honesto.
    projeto: null,
    modelos: modelos.length,
    entidades: modelos.reduce((s, m) => s + (m.entityCount ?? 0), 0),
    porClasse: [...porClasse.entries()]
      .map(([classe, contagem]) => ({ classe, contagem }))
      .sort((a, b) => b.contagem - a.contagem || a.classe.localeCompare(b.classe)),
    pisos: [...new Set(pisos)],
    topicos: topicos.map((t) => ({
      titulo: t.title,
      estado: t.status,
      responsavel: null,
    })),
    data: agora,
  }
}
